import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  Subsubtype,
  ServiceType,
  Subtype,
} from '../../types/ServiceAccordion.type';
import {
  getSubSubtypeFormConfiguration,
  listServiceTree,
} from '../../utils/serviceFormConfigurationApi';
import TreeBranch, { treeItemSx } from '../common/TreeBranch';

type PackageSelectorDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelectPackage: (
    packageItem: Subsubtype,
    configuration: Awaited<ReturnType<typeof getSubSubtypeFormConfiguration>>,
    context: {
      serviceType: ServiceType;
      subtype: Subtype;
    },
  ) => Promise<void> | void;
  selectedPackageId?: string;
};

type FilteredService = Omit<ServiceType, 'subtypes'> & {
  subtypes: Array<
    ServiceType['subtypes'][number] & {
      subsubtypes: Subsubtype[];
    }
  >;
};

const PackageSelectorDialog = ({
  open,
  onClose,
  onSelectPackage,
  selectedPackageId,
}: PackageSelectorDialogProps) => {
  const [services, setServices] = useState<ServiceType[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectingId, setSelectingId] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const loadServices = async () => {
    setLoading(true);
    setError('');
    try {
      setServices(await listServiceTree());
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Unable to load packages.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setExpanded({});
    setSearchTerm('');
    void loadServices();
  }, [open]);

  const filteredServices = useMemo<FilteredService[]>(() => {
    const query = searchTerm.trim().toLocaleLowerCase();
    if (!query) return services as FilteredService[];

    return services.reduce<FilteredService[]>((result, service) => {
      const serviceMatches = service.name.toLocaleLowerCase().includes(query);
      const subtypes = service.subtypes.reduce<FilteredService['subtypes']>(
        (subtypeResult, subtype) => {
          const subtypeMatches = subtype.name
            .toLocaleLowerCase()
            .includes(query);
          const packages =
            serviceMatches || subtypeMatches
              ? subtype.subsubtypes
              : subtype.subsubtypes.filter((packageItem) =>
                  packageItem.name.toLocaleLowerCase().includes(query),
                );

          if (packages.length > 0) {
            subtypeResult.push({ ...subtype, subsubtypes: packages });
          }
          return subtypeResult;
        },
        [],
      );

      if (subtypes.length > 0) {
        result.push({ ...service, subtypes });
      }
      return result;
    }, []);
  }, [searchTerm, services]);

  const toggle = (key: string) => {
    setExpanded((current) => ({ ...current, [key]: !current[key] }));
  };

  const selectPackage = async (
    packageItem: Subsubtype,
    serviceType: ServiceType,
    subtype: Subtype,
  ) => {
    if (!packageItem.hasFormConfiguration || selectingId) return;

    setSelectingId(packageItem.id);
    setError('');
    try {
      const configuration = await getSubSubtypeFormConfiguration(
        packageItem.id,
      );
      if (!configuration || configuration.sections.length === 0) {
        setError('This package does not have a form configuration yet.');
        return;
      }
      await onSelectPackage(packageItem, configuration, { serviceType, subtype });
    } catch (selectError) {
      setError(
        selectError instanceof Error
          ? selectError.message
          : 'Unable to load this package configuration.',
      );
    } finally {
      setSelectingId('');
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      aria-labelledby="package-selector-dialog-title"
    >
      <Box
        sx={{
          p: 2,
          minHeight: 420,
          maxHeight: '75vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        aria-labelledby="package-selector-dialog-title"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}
        >
          <Box>
            <Typography
              id="package-selector-dialog-title"
              variant="h6"
              fontWeight={700}
            >
              Select package
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a configured package to import its questions.
            </Typography>
          </Box>
          <IconButton
            type="button"
            aria-label="Close package selector"
            onClick={onClose}
          >
            <X size={20} />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          size="small"
          value={searchTerm}
          placeholder="Search service, type, or package"
          onChange={(event) => setSearchTerm(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            mt: 1.5,
            pr: 0.5,
            scrollbarWidth: 'thin',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredServices.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ py: 4, textAlign: 'center' }}
            >
              {searchTerm ? 'No matching packages.' : 'No services available.'}
            </Typography>
          ) : (
            <List disablePadding>
              {filteredServices.map((service) => {
                const serviceKey = `service-${service.id}`;
                const serviceOpen = expanded[serviceKey] ?? false;

                return (
                  <Box key={service.id}>
                    <ListItem
                      disablePadding
                      sx={{
                        mb: 0,
                        backgroundColor: 'background.paper',
                        borderRadius: 0,
                        boxShadow: 'none',
                        borderBottom: '1px solid #dce3e8',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemButton
                        dense
                        onClick={() => toggle(serviceKey)}
                        sx={{ minHeight: 42 }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              variant="h6"
                              component="div"
                              fontWeight="bold"
                              color="text.primary"
                            >
                              {service.name}
                            </Typography>
                          }
                        />
                        {serviceOpen ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronRight size={20} />
                        )}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={serviceOpen} timeout="auto" unmountOnExit>
                      <TreeBranch sx={{ pt: 2, pb: 1 }}>
                        <Typography
                          variant="body1"
                          component="h3"
                          fontWeight="bold"
                          color="text.primary"
                          sx={{ mb: 2 }}
                        >
                          {service.name} Service Types
                        </Typography>

                        {service.subtypes.map((subtype) => {
                          const subtypeKey = `subtype-${subtype.id}`;
                          const subtypeOpen = expanded[subtypeKey] ?? false;
                          return (
                            <Box key={subtype.id}>
                              <ListItem
                                disablePadding
                                sx={[
                                  {
                                    mb: 1,
                                    minHeight: 42,
                                    backgroundColor: 'background.paper',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': {
                                      backgroundColor: 'action.hover',
                                    },
                                  },
                                  treeItemSx,
                                ]}
                              >
                                <ListItemButton
                                  dense
                                  onClick={() => toggle(subtypeKey)}
                                  sx={{ minHeight: 40 }}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color="text.primary"
                                      >
                                        {subtype.name}
                                      </Typography>
                                    }
                                  />
                                  {subtypeOpen ? (
                                    <ChevronDown size={18} />
                                  ) : (
                                    <ChevronRight size={18} />
                                  )}
                                </ListItemButton>
                              </ListItem>
                              <Collapse
                                in={subtypeOpen}
                                timeout="auto"
                                unmountOnExit
                              >
                                <TreeBranch sx={{ mb: 1 }}>
                                  <Typography
                                    variant="body2"
                                    component="h4"
                                    fontWeight="bold"
                                    color="text.primary"
                                    sx={{ mb: 1.5 }}
                                  >
                                    {subtype.name} Packages
                                  </Typography>

                                  {subtype.subsubtypes.map((packageItem) => {
                                    const isSelected =
                                      selectedPackageId === packageItem.id;
                                    const isSelecting =
                                      selectingId === packageItem.id;
                                    return (
                                      <ListItem
                                        key={packageItem.id}
                                        disablePadding
                                        sx={[
                                          {
                                            mb: 1,
                                            minHeight: 42,
                                            backgroundColor: 'background.paper',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: isSelected
                                              ? 'primary.main'
                                              : 'divider',
                                            '&:hover': {
                                              backgroundColor: 'action.hover',
                                            },
                                          },
                                          treeItemSx,
                                        ]}
                                      >
                                        <ListItemButton
                                          dense
                                          disabled={
                                            !packageItem.hasFormConfiguration ||
                                            Boolean(selectingId)
                                          }
                                          selected={isSelected}
                                          onClick={() =>
                                            void selectPackage(
                                              packageItem,
                                              service,
                                              subtype,
                                            )
                                          }
                                          sx={{ minHeight: 40 }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Typography
                                                variant="body2"
                                                color="text.primary"
                                              >
                                                {packageItem.name}
                                              </Typography>
                                            }
                                            secondary={
                                              packageItem.hasFormConfiguration
                                                ? 'Configuration available'
                                                : 'Not configured'
                                            }
                                          />
                                          {isSelecting ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            <Chip
                                              size="small"
                                              label={
                                                packageItem.hasFormConfiguration
                                                  ? 'Configured'
                                                  : 'Not configured'
                                              }
                                              color={
                                                packageItem.hasFormConfiguration
                                                  ? 'success'
                                                  : 'default'
                                              }
                                              variant={
                                                packageItem.hasFormConfiguration
                                                  ? 'filled'
                                                  : 'outlined'
                                              }
                                            />
                                          )}
                                        </ListItemButton>
                                      </ListItem>
                                    );
                                  })}
                                </TreeBranch>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </TreeBranch>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default PackageSelectorDialog;
